import Controller from '@ember/controller';
import { inject as service } from '@ember/service';
import { computed } from '@ember/object';
import SqlImportModel, { SyncTarget } from 'cio/models/sql-import';
import { ImportType, SqlImportDatabase } from 'cio/utils/sql-import';
import AuthService from 'cio/services/auth';
import AccountService from 'cio/services/account';
import { sqlImportDatabaseInfo } from 'cio/helpers/sql-import-database-info';
import ShortSegmentModel from 'cio/models/short-segment';
import DS from 'ember-data';
import ObjectTypeModel from 'cio/models/object-type';

type DecoupledSqlIntegrations = {
  objects: Record<string, SqlImportModel[]>;
  people: SqlImportModel[];
};

export default class SqlImportsIndexController extends Controller {
  @service auth!: AuthService;
  @service account!: AccountService;
  @service store!: DS.Store;

  readonly SyncTarget = SyncTarget;

  declare model: { type: SqlImportDatabase; sqlImports: SqlImportModel[]; segments: ShortSegmentModel };

  tooltipText = `You must have Workspace manager permissions (or greater) to manage syncs. Contact your workspace admin to increase your permissions.`;

  @computed('model.sqlImports.@each.isNew')
  get imports() {
    return this.model.sqlImports.filterBy('isNew', false as any);
  }

  @computed('model.sqlImports.@each.isNew')
  get groupedImports() {
    return this.decoupleSqlObjectIntegrations(this.model.sqlImports);
  }

  get showPremiumLabel() {
    return this.account.planFeatures.warehouseSync.showPremiumLabel;
  }

  get blankSlateTitle() {
    return `Sync people or custom objects from your ${sqlImportDatabaseInfo([
      'name',
      this.model.type,
    ])} ${sqlImportDatabaseInfo(['store', this.model.type])}`;
  }

  private decoupleSqlObjectIntegrations(sqlImportModel: SqlImportModel[]) {
    let imports = sqlImportModel.reduce(
      (acc, val) => {
        if (val.importType === ImportType.Object) {
          let objectType = val.belongsTo('objectType').value() as ObjectTypeModel;
          let objectName = objectType?.name ?? 'object';

          acc.objects[objectName] ||= [];
          acc.objects[objectName].push(val);
        } else {
          acc.people.push(val);
        }

        return acc;
      },
      { objects: {}, people: [] } as DecoupledSqlIntegrations,
    );

    let sortedImports = { people: [], objects: {} } as DecoupledSqlIntegrations;

    sortedImports.people = imports.people.filterBy('isNew', false as any);
    for (let key in imports.objects) {
      sortedImports.objects[key] = imports.objects[key].filterBy('isNew', false as any);
    }

    return sortedImports;
  }
}

declare module '@ember/controller' {
  interface Registry {
    'sql-imports.index': SqlImportsIndexController;
  }
}
